import React, { useEffect, useMemo, useState } from "react";
import {
  Button,
  Flex,
  FormControl,
  FormLabel,
  Grid,
  Text,
  Textarea,
  useColorModeValue
} from "@chakra-ui/react";
import Card from "components/Card/Card";
import CardBody from "components/Card/CardBody";
import CardHeader from "components/Card/CardHeader";
import { useTranslation } from "react-i18next";
import { ROUTES } from "../../../constants/routes-path";
import { useNavigate, useParams } from "react-router-dom";
import { useCustomToast } from "../../../components/CustomToast/CustomToast";
import { checkErrors, hasError } from "../../../utils/forms-utils";
import { processesService } from "../../../services/processesService";

function ProcessArchive({ data }) {
  const textColor = useColorModeValue("gray.700", "white");
  const bgTextarea = useColorModeValue("white", "navy.900");
  const placeholderColor = useColorModeValue("gray.300", "gray.400");

  const { t } = useTranslation();
  const navigate = useNavigate();
  const showToast = useCustomToast();
  const { id } = useParams();

  const [inputError, setInputError] = useState({});

  const ids = useMemo(() => ({
    reason: "process-archive-reason",
  }), []);

  const [formData, setFormData] = useState({ reason: "" });

  const isFormInvalid = formData && inputError ? hasError({ formData, inputError }) : true;

  const initFormData = (data) => ({
    [ids.reason]: {
      value: data?.motivo_arquivamento ?? "",
      validations: [{ required: true }]
    }
  });

  useEffect(() => {
    if (id) {
      fetchProcess();
    }
  }, [id]);

  const fetchProcess = async () => {
    try {
      const process = await processesService.find(id);
      if (!process) {
        showToast({ title: t("process.messages.errors.find"), status: "error" });
        redirectToList();
      }
      setFormData(initFormData(process));
    } catch (error) {
      showToast({ title: t("process.messages.errors.find"), status: "error" });
      redirectToList();
    }
  };

  const handleInputChange = (e) => {
    const { id, type } = e.target;
    const value = type === "checkbox" ? e.target.checked : e.target.value;

    const newFormData = {
      ...formData,
      [id]: {
        ...formData[id],
        value: value
      }
    };
    setFormData(newFormData);
    setInputError(checkErrors({ formData: newFormData, inputError, name: id }));
  };

  const redirectToList = () => {
    navigate(ROUTES.processesPath);
  }

  const toDTO = () => ({
    motivo_arquivamento: formData[ids.reason].value,
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isFormInvalid) return;

    try {
      const data = toDTO();

      await processesService.archive(id, data);

      showToast({ title: t("system.notifications.success"), status: "success" });
      redirectToList();
    } catch (error) {
      console.log(error)
      showToast({ title: t(error), status: "error" });
    }
  };

  return (
    <Flex direction="column" minH="89vh" align="center" pt={{sm: "120px", md: "75px"}}>
      <Card mt={"80px"}>
        <CardHeader mb="40px">
          <Flex direction="column">
            <Text color={textColor} fontSize="lg" fontWeight="bold" mb="3px">
              {t("process.archive.form.title")}
            </Text>
            <Text color="gray.400" fontWeight="normal" fontSize="sm">
              {t("process.archive.form.subtitle")}
            </Text>
          </Flex>
        </CardHeader>
        <CardBody>
          <form onSubmit={handleSubmit}>
            <Flex direction="column" w="100%">
              <Grid gap="24px">
                <FormControl isRequired isInvalid={inputError[ids.reason]}>
                  <FormLabel color={textColor} fontWeight="bold" fontSize="xs">
                    {t("process.archive.form.fields.reason.label")}
                  </FormLabel>
                  <Textarea
                    minH="120px"
                    name="reason"
                    id={ids.reason}
                    onChange={handleInputChange}
                    bg={bgTextarea}
                    _placeholder={{color: placeholderColor}}
                    placeholder={t("process.archive.form.fields.reason.placeholder")}
                    fontSize="xs"
                  />
                </FormControl>
              </Grid>
              <Flex justify="space-between">
                <Button
                  variant="light"
                  fontSize="sm"
                  alignSelf="flex-end"
                  mt="24px"
                  w="100px"
                  h="35px"
                  onClick={redirectToList}
                >
                  {t("system.actions.back")}
                </Button>
                <Button
                  variant="dark"
                  fontSize="sm"
                  alignSelf="flex-end"
                  type="submit"
                  mt="24px"
                  w="100px"
                  h="35px"
                  isDisabled={isFormInvalid}
                  _hover={isFormInvalid ? {} : undefined}
                >
                  {t("system.actions.save")}
                </Button>
              </Flex>
            </Flex>
          </form>
        </CardBody>
      </Card>
    </Flex>
  );
}

export default ProcessArchive